import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {DunningData} from "../../payment-plan/model/dunning-data.interface";
import {BalanceEntry} from "../../payment-plan/model/balance-entry.interface";
import {ChargeAdjustment} from "../../payment-plan/model/charge-adjustment.interface";
import {Contract} from "../../payment-plan/model/contract.interface";
import {Charge} from "../../payment-plan/model/charge.interface";
import {OneTimePayment} from "../../payment-plan/model/one-time-payment.interface";
import {MOCK_DUNNING_DATA_4} from "../../payment-plan/mock/mock-dunning-data-4";
import {ManualPayment} from "../model/manual-payment.interface";

@Injectable({
    providedIn: 'root'
})
export class BmDebtCollectionService {

    // Subjects for reactive updates
    private dunningDataSubject = new BehaviorSubject<DunningData | null>(null);
    private balanceEntriesSubject = new BehaviorSubject<BalanceEntry[]>([]);
    private chargeAdjustmentsSubject = new BehaviorSubject<ChargeAdjustment[]>([]);
    private selectedContractSubject = new BehaviorSubject<Contract | null>(null);
    // Observables for components to subscribe to
    public dunningData$: Observable<DunningData | null> = this.dunningDataSubject.asObservable();
    public balanceEntries$: Observable<BalanceEntry[]> = this.balanceEntriesSubject.asObservable();
    selectedContract$ = this.selectedContractSubject.asObservable();

    constructor() {
    }

    /**
     * Initialize the payment plan with dunning data
     * @param dunningId The dunning ID to load data for
     */
    async initializeDebtCollection(dunningId: number): Promise<void> {
        try {
            const dunningData = await this.loadDunningData(dunningId);
            this.dunningDataSubject.next(dunningData);

            // Initialize payment plan with initial charges
            await this.initializeBalanceWithCharges(dunningData);
        } catch (error) {
            console.error('Error initializing payment plan:', error);
            throw error;
        }
    }

    /**
     * Load dunning data from API (to be implemented)
     * For now, this is a placeholder - will be overridden by mock service
     */
    async loadDunningData(dunningId: number): Promise<DunningData> {
        // This will be implemented to call the actual API
        // For testing, the mock service will override this
        // return new Promise((resolve) => {
        //   setTimeout(() => {
        //     resolve({
        //       customerId: 0,
        //       customerBillingAccountId: 0,
        //       dunningId: dunningId,
        //       contracts: [],
        //       charges: []
        //     });
        //   }, 100);
        // });

        return new Promise((resolve, reject) => {
            // Simulate API delay
            setTimeout(() => {
                resolve(MOCK_DUNNING_DATA_4);
            }, 500);
        });
    }

    /**
     * Initialize balance entries with charges from dunning data
     */
    private async initializeBalanceWithCharges(dunningData: DunningData): Promise<void> {
        const entries: BalanceEntry[] = dunningData.charges.map((charge, index) => ({
            id: `charge-${index}-${Date.now()}`,
            type: 'charge',
            name: charge.name,
            amount: charge.amount,
            currency: charge.currency,
            timestamp: new Date(),
            removable: charge.deductable,
            active: false
        }));

        this.balanceEntriesSubject.next(entries);
    }

    /**
     * Remove contract activity
     */
    async removeContractActivity(contractIdentifier: string): Promise<void> {
        // Remove from balance
        await this.removeBalanceEntry(`contract-${contractIdentifier}`);
    }

    /**
     * Adjust a charge (remove or reduce)
     */
    async adjustCharge(charge: Charge, adjustmentType: 'remove' | 'reduce', newAmount?: number): Promise<void> {
        const currentAdjustments = this.chargeAdjustmentsSubject.value;

        // Remove existing adjustment for this charge
        const filteredAdjustments = currentAdjustments.filter(
            adj => adj.charge.name !== charge.name
        );

        if (adjustmentType === 'remove') {
            // Create removal adjustment
            const adjustment: ChargeAdjustment = {
                charge: charge,
                adjustmentType: 'remove',
                newAmount: 0,
                currency: charge.currency
            };

            this.chargeAdjustmentsSubject.next([...filteredAdjustments, adjustment]);

            // Remove from balance
            await this.removeBalanceEntryByName(charge.name);

        } else if (adjustmentType === 'reduce' && newAmount !== undefined) {
            // Create reduction adjustment
            const adjustment: ChargeAdjustment = {
                charge: charge,
                adjustmentType: 'reduce',
                newAmount: newAmount,
                currency: charge.currency
            };

            this.chargeAdjustmentsSubject.next([...filteredAdjustments, adjustment]);

            // Update balance entry
            await this.updateBalanceEntryAmount(charge.name, newAmount);
        }
    }

    /**
     * Restore a charge (undo adjustment)
     */
    async restoreCharge(charge: Charge): Promise<void> {
        const currentAdjustments = this.chargeAdjustmentsSubject.value;

        // Remove adjustment
        const filteredAdjustments = currentAdjustments.filter(
            adj => adj.charge.name !== charge.name
        );
        this.chargeAdjustmentsSubject.next(filteredAdjustments);

        // Restore original charge in balance
        const currentEntries = this.balanceEntriesSubject.value;
        const existingEntry = currentEntries.find(entry => entry.name === charge.name);

        if (existingEntry) {
            // Update existing entry
            await this.updateBalanceEntryAmount(charge.name, charge.amount);
        } else {
            // Add back to balance
            const newEntry: BalanceEntry = {
                id: `charge-${charge.name}-${Date.now()}`,
                type: 'charge',
                name: charge.name,
                amount: charge.amount,
                currency: charge.currency,
                timestamp: new Date(),
                removable: charge.deductable,
                active: false
            };
            this.balanceEntriesSubject.next([...currentEntries, newEntry]);
        }
    }

    /**
     * Add a one-time payment
     */
    async addOneTimePayment(payment: OneTimePayment): Promise<void> {

        // Add to balance as negative amount (reduces debt)
        const newEntry: BalanceEntry = {
            id: `otp-${Date.now()}`,
            type: 'one-time-payment',
            name: payment.name,
            amount: -payment.amount, // negative because it reduces debt
            currency: payment.currency,
            timestamp: payment.plannedDate,
            removable: true,
            active: false
        };

        this.balanceEntriesSubject.next([...this.balanceEntriesSubject.value, newEntry]);

    }

    /**
     * Remove a one-time payment
     */
    async removeOneTimePayment(paymentName: string): Promise<void> {
        // Remove from balance
        await this.removeBalanceEntryByName(paymentName);
    }

    /**
     * Remove a balance entry by ID
     */
    private async removeBalanceEntry(id: string): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;
        this.balanceEntriesSubject.next(currentEntries.filter(entry => entry.id !== id));
    }

    /**
     * Remove a balance entry by name
     */
    private async removeBalanceEntryByName(name: string): Promise<void> {
        const updatedEntries = this.balanceEntriesSubject.value.map(entry =>
            entry.name === name
                ? { ...entry, active: false }
                : entry
        );
        this.balanceEntriesSubject.next(updatedEntries);
    }

    /**
     * Update balance entry amount
     */
    private async updateBalanceEntryAmount(name: string, newAmount: number): Promise<void> {
        const currentEntries = this.balanceEntriesSubject.value;
        const updatedEntries = currentEntries.map(entry => {
            if (entry.name === name) {
                return {...entry, amount: newAmount, active: true};
            }

            return entry;
        });
        this.balanceEntriesSubject.next(updatedEntries);
    }

    /**
     * Get current monthly price for a contract
     */
    private getCurrentMonthlyPrice(contract: Contract): number {
        if (!contract.monthlyPrice || contract.monthlyPrice.length === 0) {
            return 0;
        }

        // Get the most recent price (last entry in array)
        // In a real implementation, you'd compare dates
        const latestPrice = contract.monthlyPrice[contract.monthlyPrice.length - 1];
        return latestPrice.monthlyPrice;
    }

    /**
     * Round to 2 decimal places (matching BigDecimal precision)
     */
    private roundToTwoDecimals(value: number): number {
        return Math.round(value * 100) / 100;
    }

    /**
     * Get current dunning data
     */
    getDunningData(): DunningData | null {
        return this.dunningDataSubject.value;
    }

    toggleCharge(charge: Charge): void {
        const entries = this.balanceEntriesSubject.value;
        const existing = entries.find(e => e.name === charge.name);

        if (!existing) {
            this.balanceEntriesSubject.next([
                ...entries,
                {
                    id: `charge-${charge.name}`,
                    name: charge.name,
                    type: 'charge',
                    amount: charge.amount,
                    currency: charge.currency,
                    active: true,
                    timestamp: new Date(),
                    removable: true
                }
            ]);
        } else {
            existing.active = !existing.active;
            existing.amount = charge.amount;
            existing.currency = charge.currency;
            this.balanceEntriesSubject.next([...entries]);
        }

    }

    isChargeActive(chargeName: string): boolean {
        return this.balanceEntriesSubject.value.some(
            e => e.name === chargeName && e.active
        );
    }

    changeContractMonths(
        contractId: string,
        operation: 'inc' | 'dec'
    ): void {
        const dunningData = this.dunningDataSubject.value;
        if (!dunningData) return;

        const contract = dunningData.contracts.find(
            c => c.identifier === contractId
        );
        if (!contract) return;

        // Update months
        const currentMonths = contract.dunningMonths ?? 0;
        const newMonths =
            operation === 'inc'
                ? currentMonths + 1
                : Math.max(0, currentMonths - 1);

        contract.dunningMonths = newMonths;

        // Calculate dunning amount
        const monthlyPrice = this.getCurrentMonthlyPrice(contract);
        contract.dunningAmount = this.roundToTwoDecimals(
            newMonths * monthlyPrice
        );

        // Notify subscribers
        this.dunningDataSubject.next({ ...dunningData });
    }

    /**
     * Add Manual payment
     */
    async addManualPayment(payment: ManualPayment): Promise<void> {
        // Add to balance as negative amount (reduces debt)
        const newEntry: BalanceEntry = {
            id: `otp-${Date.now()}`,
            type: 'one-time-payment',
            name: payment.name,
            amount: payment.amount, // negative because it reduces debt
            currency: payment.currency,
            timestamp: payment.plannedDate,
            removable: true,
            active: true
        };

        this.balanceEntriesSubject.next([...this.balanceEntriesSubject.value, newEntry]);
    }

    setSelectedContract(contract: Contract): void {
        this.selectedContractSubject.next(contract);
    }

    clearSelectedContract(): void {
        this.selectedContractSubject.next(null);
    }

    /**
     * Reset the service state
     */
    reset(): void {
        this.dunningDataSubject.next(null);
        this.balanceEntriesSubject.next([]);
        this.chargeAdjustmentsSubject.next([]);
        this.selectedContractSubject.next(null);
    }
}
